// ═══════════════════════════════════════════════════════════════════════════
// Remisiones contra Postgres — Etapa 4b
//
// LA REGLA QUE GOBIERNA ESTE MÓDULO: el folio fiscal EXIGE conexión.
//
// Un consecutivo sin huecos ni repetidos necesita una sola autoridad, y esa
// autoridad es el servidor. No es una limitación técnica que podamos rodear:
// dos dispositivos sin red asignando folios producen exactamente el duplicado
// del folio 376.
//
// Todo lo demás sigue funcionando sin conexión: la remisión se guarda en el
// dispositivo con su identidad propia y queda PENDIENTE DE FOLIO. Al
// sincronizar, el servidor le asigna el suyo.
// ═══════════════════════════════════════════════════════════════════════════

import { supabase } from "../auth/sesion.js";
import { encolar } from "./bitacora.js";

/** Convierte una remisión de la app en los argumentos de crear_remision(). */
export function argumentosDeRemision(inv, tenantId) {
  return {
    p_tenant: tenantId,
    p_local_id: inv.id,                       // identidad creada en el dispositivo
    p_client_legacy: inv.client?.id,
    p_lines: (inv.lines || [])
      .filter(l => (l.qty || 0) > 0)
      .map(l => ({
        product_legacy: l.productId,
        qty: l.qty,
        // De esas botellas, cuántas van sin cobrar. El servidor calcula el
        // dinero sobre (qty − cortesía) y descuenta del inventario qty
        // completo: salieron todas (migración 0060).
        courtesy_qty: l.cortesia || 0,
        unit_price: l.unitPrice ?? (l.qty ? l.amount / l.qty : 0),
        ieps_rate: l.iepsRate ?? 0,
        description: l.productName || "",
      })),
    p_issued_at: inv.date || new Date().toISOString(),
    p_discount: inv.discount ?? 0,
    p_iva_rate: inv.ivaRate ?? 16,
    p_notes: inv.notes || null,
    p_forma_pago: inv.formaPago || null,
    p_metodo_pago: inv.metodoPago || null,
    p_uso_cfdi: inv.usoCfdi || null,
    // Con cuál de los regímenes del cliente se emite ESTA. Sin él, el
    // servidor usa el del cliente, que es lo que hacía hasta 0061.
    p_regimen_fiscal: inv.regimenFiscal || null,
    p_warehouse: inv.warehouse || null,
    p_total_cliente: inv.total ?? null,       // para que el servidor lo verifique
    p_device: (() => { try { return localStorage.getItem("vt-device-id"); } catch { return null; } })(),
  };
}

/**
 * Crea la remisión en Postgres. Devuelve { folio, total, aviso, … }.
 * Lanza si falla — quien llama decide si encolar.
 */
export async function crearEnPostgres(inv, tenantId) {
  const { data, error } = await supabase.rpc("crear_remision", argumentosDeRemision(inv, tenantId));
  if (error) throw error;
  return data;
}

/**
 * Guarda la remisión pidiendo folio al servidor si hay conexión.
 *
 * @returns { remision, folio, pendiente, aviso }
 *          pendiente = true → se guardó local, sin folio, encolada
 *
 * `crear` se inyecta —igual que `crearDoc` en los generadores de PDF— para
 * poder probar qué se hace con cada respuesta del servidor sin levantar red.
 * Lo que importa aquí no es la llamada: es la decisión que se toma después,
 * y era justo ahí donde estaba el defecto.
 */
export async function guardarRemision(inv, tenantId, crear = crearEnPostgres) {
  // Sin red: se guarda local y se encola. NUNCA se inventa un folio.
  if (!navigator.onLine) {
    encolarRemision(inv, tenantId);
    return { remision: { ...inv, folio: null, _pendienteFolio: true },
             pendiente: true, folio: null };
  }

  try {
    const r = await crear(inv, tenantId);

    // ── El servidor YA TENÍA esta remisión ────────────────────────────────
    //
    // `crear_remision` es idempotente por `legacy_id`: si la encuentra, la
    // devuelve TAL CUAL y descarta las líneas que se le mandaron. Es lo
    // correcto —así un reintento tras un tiempo de espera agotado no duplica
    // nada— pero significa que NO SE GUARDÓ NADA NUEVO.
    //
    // Antes esto se devolvía en `yaExistia` y nadie lo miraba, así que la
    // pantalla decía "✅ Remisión reemplazada" sobre una venta que el servidor
    // nunca recibió. Peor: la respuesta idempotente trae solo
    // {id, folio, total, ya_existia}, y las seis líneas de abajo hacían
    // `Number(undefined)` → la remisión local quedaba con NaN en subtotal,
    // IEPS, IVA y botellas, y de ahí el ajuste de existencias salía en NaN.
    //
    // Ahora se devuelve la remisión DEL SERVIDOR sin inventar campos, y se
    // avisa. Reemplazar una remisión no existe en Postgres: no hay ninguna
    // función que actualice una: solo `crear_remision`,
    // `cambiar_forma_de_pago` y `aplicar_saldo_a_favor`.
    if (r.ya_existia) {
      return {
        remision: { ...inv, folio: r.folio, total: Number(r.total), _pgId: r.id },
        folio: r.folio,
        pendiente: false,
        yaExistia: true,
        aviso: `El servidor ya tenía la remisión #${r.folio} y la conserva sin ` +
               `cambios: NO se guardó nada de lo que acabas de capturar. ` +
               `Si hay que corregirla, ábrela con «Editar».`,
      };
    }

    return {
      remision: {
        ...inv,
        folio: r.folio,
        // El servidor calcula en numeric exacto: sus totales mandan.
        subtotalBeforeDiscount: Number(r.subtotal_before_disc),
        discountAmount: Number(r.discount_amount),
        subtotal: Number(r.subtotal),
        iepsAmount: Number(r.ieps_amount),
        ivaAmount: Number(r.iva_amount),
        total: Number(r.total),
        totalBottles: Number(r.total_bottles),
        iepsBreakdown: r.ieps_breakdown || [],
        _pgId: r.id,
      },
      folio: r.folio,
      pendiente: false,
      aviso: r.aviso || null,
      yaExistia: false,
    };
  } catch (e) {
    // Falló con red disponible: puede ser un tropiezo pasajero o un rechazo.
    // En ambos casos se guarda local y se encola — no se pierde trabajo.
    console.warn("[remisión] no se pudo crear en Postgres:", e?.message);
    encolarRemision(inv, tenantId);
    return { remision: { ...inv, folio: null, _pendienteFolio: true },
             pendiente: true, folio: null, error: e?.message };
  }
}

// ═══════════════════════════════════════════════════════════════════════════
// Editar una remisión (migración 0064)
//
// El operador: "Debería ser posible editar las remisiones, es algo sujeto a
// errores. Internamente puede quedar en el log lo que cambió."
//
// Antes no existía: Editar llamaba a `crear_remision`, que devolvía la
// original sin cambios, y lo capturado se perdía con un aviso de por medio.
// `editar_remision` corrige el documento sin cambiarle folio ni fecha,
// recalcula con el mismo cálculo que al crear, ajusta el inventario por la
// diferencia y deja el antes y el después en la bitácora.
//
// EXIGE CONEXIÓN y no se encola. Una corrección que se aplica horas después
// puede pisar otra, y el PDF que sale tiene que ser el que guardó el servidor.
// ═══════════════════════════════════════════════════════════════════════════

/** Los argumentos de editar_remision(): los de crear, sin folio ni fecha. */
export function argumentosDeEdicion(inv, tenantId) {
  const a = argumentosDeRemision(inv, tenantId);
  return {
    p_tenant: a.p_tenant, p_legacy: inv.id, p_client_legacy: a.p_client_legacy,
    p_lines: a.p_lines, p_discount: a.p_discount, p_iva_rate: a.p_iva_rate,
    p_notes: a.p_notes, p_forma_pago: a.p_forma_pago, p_metodo_pago: a.p_metodo_pago,
    p_uso_cfdi: a.p_uso_cfdi, p_warehouse: a.p_warehouse,
    p_total_cliente: a.p_total_cliente, p_device: a.p_device,
    p_regimen_fiscal: a.p_regimen_fiscal,
  };
}

export async function editarEnPostgres(inv, tenantId) {
  const { data, error } = await supabase.rpc("editar_remision", argumentosDeEdicion(inv, tenantId));
  if (error) throw error;
  return data;
}

/**
 * Guarda la corrección. Devuelve `{ remision, aviso }` con los totales DEL
 * SERVIDOR, o lanza con un mensaje que se puede enseñar tal cual.
 *
 * `editar` se inyecta para probar la decisión sin red, igual que `crear` en
 * `guardarRemision`.
 */
export async function guardarEdicion(inv, tenantId, editar = editarEnPostgres) {
  if (!navigator.onLine)
    throw new Error("Editar una remisión necesita conexión: el servidor recalcula los " +
                    "totales y deja escrito qué cambió. Inténtalo cuando tengas señal.");
  const r = await editar(inv, tenantId);
  return {
    remision: {
      ...inv,
      folio: r.folio,
      subtotalBeforeDiscount: Number(r.subtotal_before_disc),
      discountAmount: Number(r.discount_amount),
      subtotal: Number(r.subtotal),
      iepsAmount: Number(r.ieps_amount),
      ivaAmount: Number(r.iva_amount),
      total: Number(r.total),
      totalBottles: Number(r.total_bottles),
      iepsBreakdown: r.ieps_breakdown || [],
      warehouse: r.warehouse_id || inv.warehouse,
      _pgId: r.id,
    },
    aviso: r.aviso || null,
  };
}

function encolarRemision(inv, tenantId) {
  encolar({
    tabla: "remision",
    operacion: "crear_remision",
    clave: inv.id,
    datos: argumentosDeRemision(inv, tenantId),
  });
}

/** ¿Esta remisión sigue esperando folio? */
export const esperaFolio = (inv) => inv?._pendienteFolio === true || inv?.folio == null;

/** Etiqueta para mostrar donde iría el folio. */
export const etiquetaFolio = (inv) =>
  esperaFolio(inv) ? "PENDIENTE" : String(inv.folio);

// ═══════════════════════════════════════════════════════════════════════════
// Pagos
// ═══════════════════════════════════════════════════════════════════════════

/** Convierte un pago de la app en los argumentos de registrar_pago(). */
export function argumentosDePago(pago, remision, tenantId) {
  return {
    p_tenant: tenantId,
    p_local_id: pago.id,                 // ya nace con id en cobranza.js
    p_remision_legacy: remision.id,
    p_amount: Number(pago.monto),
    p_occurred_at: pago.fecha || new Date().toISOString(),
    p_method: pago.metodo || null,
    p_account_label: pago.cuenta || null,
    p_note: pago.nota || null,
    p_reverses_local: pago.revierteA || null,
    p_device: (() => { try { return localStorage.getItem("vt-device-id"); } catch { return null; } })(),
  };
}

/**
 * Encola el pago para que suba solo. NO se intenta en línea aquí a propósito:
 * un pago debe quedar registrado al instante, y su viaje al servidor es
 * asunto del sincronizador. Si la remisión aún va por delante en la cola, el
 * orden FIFO garantiza que llegue primero.
 */
export function encolarPago(pago, remision, tenantId) {
  encolar({
    tabla: "payment",
    operacion: "registrar_pago",
    clave: pago.id,
    datos: argumentosDePago(pago, remision, tenantId),
  });
}

// ═══════════════════════════════════════════════════════════════════════════
// Cancelación
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Marca la remisión como cancelada. NUNCA la borra.
 *
 * Funciona con Postgres apagado: el marcado local es la parte que cierra el
 * escenario de "emitir, cobrar en efectivo y borrar". Si Postgres está
 * encendido, además se encola para que el servidor devuelva el inventario y
 * deje registro en la bitácora de auditoría.
 */
export function cancelarRemision(inv, { motivo, tenantId, usarPostgres }) {
  const razon = (motivo || "").trim();
  if (!razon) throw new Error("La cancelación exige un motivo.");

  const cancelada = {
    ...inv,
    canceledAt: new Date().toISOString(),
    cancelReason: razon,
  };

  if (usarPostgres && tenantId) {
    encolar({
      tabla: "remision",
      operacion: "cancelar_remision",
      clave: `cancel_${inv.id}`,
      datos: {
        p_tenant: tenantId,
        p_legacy: inv.id,
        p_motivo: razon,
        p_device: (() => { try { return localStorage.getItem("vt-device-id"); } catch { return null; } })(),
      },
    });
  }

  return cancelada;
}

export const estaCancelada = (inv) => !!inv?.canceledAt;


/**
 * Cambiar cómo se paga una remisión que todavía no se timbra.
 *
 * NO va por la bitácora: se aplica contra el servidor en el momento. Es un
 * cambio que se hace justo antes de timbrar —"esto va a ser en parcialidades"—
 * y encolarlo dejaría al usuario timbrando con el dato viejo, que es
 * exactamente el problema que esto viene a quitar.
 */
export async function cambiarFormaDePago(tenantId, legacyId, forma, metodo) {
  const { data, error } = await supabase.rpc("cambiar_forma_de_pago", {
    p_tenant: tenantId, p_legacy: legacyId, p_forma: forma, p_metodo: metodo,
  });
  if (error) throw new Error(error.message);
  return data;
}


/**
 * La cuenta corriente de cada cliente: cuánto entró, cuánto ampara documentos y
 * cuánto queda a su favor.
 *
 * No se guarda en ninguna parte — se calcula sumando movimientos. Un saldo
 * almacenado es un saldo que tarde o temprano se desincroniza de los asientos
 * que lo formaron.
 */
export async function saldosDeClientes(tenantId) {
  const { data, error } = await supabase.from("saldo_cliente")
    .select("client_id, cliente, recibido, aplicado, a_favor, por_cobrar")
    .eq("tenant_id", tenantId);
  if (error) throw new Error(error.message);
  return data || [];
}

/**
 * Usar saldo que YA entró para cubrir un documento.
 *
 * Sin monto, aplica lo que se pueda: lo que haya a favor, topado con lo que
 * falte en la remisión. La base acota las dos cosas — pedir de más es un error
 * de dedo, no una instrucción.
 */
export async function aplicarSaldoAFavor(tenantId, legacyId, monto = null, nota = null) {
  const { data, error } = await supabase.rpc("aplicar_saldo_a_favor", {
    p_tenant: tenantId, p_remision: legacyId, p_monto: monto, p_nota: nota,
  });
  if (error) throw new Error(error.message);
  return data;
}
